//Typesense-backed list of contact info update requests, matching the flow of
//orders/services/transactions. Used by the /customers/update_requests queue
//page (all customers) and the customer page accordion (defaultFilter on
//customerID).

import { useContext, useMemo, useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Anchor, Badge, Button, Group, Select, Text, Tooltip } from '@mantine/core';
import { HitsPerPage, InstantSearch, Pagination, SearchBox, useHits, InstantSearchProps, Configure, useSortBy, useInstantSearch } from 'react-instantsearch';
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import DataTable, { TableColumn } from 'react-data-table-component';
import { useSessionStorage } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';

import { MyUserDataContext } from '../contexts';
import { useFirestore } from '../helpers/firebaseContext';
import { CustomClaims, TypesenseUpdateRequest } from '../types';

interface InitialState {
    query?: string;
    page?: number;
}

const formatUnixDate = (seconds?: number) => seconds ? new Date(seconds * 1000).toLocaleString("en-GB", { hour12: true }) : "";

const TableDisplay = ({ indexName, showCustomer }: { indexName: string, showCustomer: boolean }) => {
    const { items } = useHits();
    const { refresh } = useInstantSearch();
    const navigate = useNavigate();
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);

    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims | undefined;
    const canExecute = !!(userClaims?.admin || userClaims?.oper || userClaims?.cr);
    const [executingID, setExecutingID] = useState<string | null>(null);

    const { refine } = useSortBy({
        items: [
            { label: 'Date (asc)', value: `${indexName}/sort/created:asc` },
            { label: 'Date (desc)', value: `${indexName}/sort/created:desc` },
        ],
    });

    const markDone = async (row: TypesenseUpdateRequest) => {
        setExecutingID(row.id);
        try {
            await updateDoc(doc(db, "customers", row.customerID, "update_requests", row.id), {
                status: "done",
                executed_by: userContext.user?.email ?? userContext.user?.uid,
                executed_at: serverTimestamp(),
            });
            //the onChangeUpdateRequest trigger syncs the doc to typesense; give it a moment
            setTimeout(() => {
                refresh();
                setExecutingID(null);
            }, 1500);
        } catch (e) {
            console.error(e);
            setExecutingID(null);
        }
    };

    const columns: TableColumn<TypesenseUpdateRequest>[] = [
        {
            name: "Date",
            id: "created",
            sortable: true,
            selector: (row) => row.created ?? 0,
            cell: (row) => formatUnixDate(row.created),
            grow: 1,
        },
        ...(showCustomer ? [{
            name: "Customer",
            id: "customerObj.name_en",
            sortable: true,
            selector: (row: TypesenseUpdateRequest) => row.customerObj?.name_en ?? "",
            cell: (row: TypesenseUpdateRequest) => (
                <Anchor size="sm" onClick={() => navigate(`/customers/${row.customerID}?tab=requests`)}>
                    {row.customerObj?.name_en || row.customerID}
                </Anchor>
            ),
            grow: 2,
        },
        {
            name: "Civil ID",
            selector: (row: TypesenseUpdateRequest) => row.customerObj?.civilID ?? "",
            grow: 1,
        }] : []),
        {
            name: "Changes",
            cell: (row) => (
                <div>
                    {row.changes?.map((change, index) => (
                        <Text key={index} size="sm">
                            <b>{change.field}</b>: {change.old_value || "—"} → {change.new_value}
                        </Text>
                    ))}
                </div>
            ),
            grow: 3,
        },
        {
            name: "Channel",
            cell: (row) => (
                <Badge variant="light" color={row.channel === "mobile_app" ? "blue" : "gray"}>
                    {row.channel === "mobile_app" ? "Mobile App" : row.channel === "link" ? "Link" : `Staff${row.assisted_by ? ` (${row.assisted_by})` : ""}`}
                </Badge>
            ),
            grow: 0.5,
        },
        {
            name: "Trading System",
            cell: (row) => row.needs_trading_system_entry ? (
                <Tooltip label={`Accounts: ${row.trading_account_ids?.join(", ") || "-"}`}>
                    <Badge color="orange" variant="light">Manual entry needed</Badge>
                </Tooltip>
            ) : (
                <Badge color="gray" variant="light">Not needed</Badge>
            ),
            grow: 1.5,
        },
        {
            name: "Status",
            cell: (row) => row.status === "pending" ? (
                <Badge color="yellow">Pending</Badge>
            ) : (
                <Tooltip label={row.executed_by ? `By ${row.executed_by} at ${formatUnixDate(row.executed_at)}` : "No manual entry was needed"}>
                    <Badge color="green">Done</Badge>
                </Tooltip>
            ),
            grow: 1,
        },
        {
            name: "",
            cell: (row) => row.status === "pending" && canExecute ? (
                <Button size="xs" variant="light" loading={executingID === row.id} onClick={() => markDone(row)}>
                    Mark entered
                </Button>
            ) : null,
            grow: 1,
        },
    ];

    return <DataTable
        columns={columns}
        sortServer
        onSort={(column, sortDirection) => {
            if (column.id) {
                refine(`${indexName}/sort/${column.id}:${sortDirection === "asc" ? "asc" : "desc"}`);
            }
        }}
        data={items as any as TypesenseUpdateRequest[]}
    />;
};

interface UpdateRequestsInstantSearchProps {
    indexName: string;
    defaultFilter?: string;
    /** show the customer name/civil ID columns and the status filter (queue view) */
    showCustomer?: boolean;
    topElements?: JSX.Element;
}

const UpdateRequestsInstantSearch = ({ indexName, defaultFilter = "", showCustomer = true, topElements }: UpdateRequestsInstantSearchProps) => {
    const userData = useContext(MyUserDataContext);

    //queue view starts on the actionable rows; customer view shows everything
    const [statusFilter, setStatusFilter] = useState<string | null>(showCustomer ? "pending" : "all");

    const typesenseSearchClient = useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userData.customSearchKey!,
            nodes: [
                {
                    host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                    port: 443,
                    protocol: 'https',
                },
            ],
        },
        additionalSearchParameters: {
            query_by: "customerObj,customerID,changed_fields,changes,trading_account_ids,channel",
            sort_by: "created:desc",
        },
    }).searchClient, [userData.customSearchKey]);

    const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
        key: 'initialUIStateFor' + indexName,
        getInitialValueInEffect: false,
    });

    const onStateChange: InstantSearchProps['onStateChange'] = ({ uiState, setUiState }) => {
        setUiState(uiState);
        setInitialUIStateForIndex(uiState[indexName]);
    };

    const filters = [
        defaultFilter,
        statusFilter && statusFilter !== "all" ? `status:=${statusFilter}` : "",
    ].filter(Boolean).join(" && ");

    return (
        <InstantSearch
            searchClient={typesenseSearchClient}
            indexName={indexName}
            initialUiState={{ [indexName]: initialUIStateForIndex }}
            onStateChange={onStateChange}
        >
            <Configure filters={filters} />

            <Group justify="space-between" align="center" style={{ marginBottom: 20 }}>
                {topElements}
                <Group>
                    <Select
                        data={[
                            { value: "pending", label: "Pending" },
                            { value: "done", label: "Done" },
                            { value: "all", label: "All" },
                        ]}
                        value={statusFilter}
                        onChange={setStatusFilter}
                        allowDeselect={false}
                        w={120}
                    />
                    <SearchBox placeholder="Search update requests" />
                </Group>
            </Group>

            <TableDisplay indexName={indexName} showCustomer={showCustomer} />

            <Group justify="space-between" style={{ marginTop: 20 }}>
                <HitsPerPage
                    items={[
                        { label: '10 per page', value: 10, default: true },
                        { label: '25 per page', value: 25 },
                        { label: '50 per page', value: 50 },
                    ]}
                />
                <Pagination />
            </Group>
        </InstantSearch>
    );
};

export default UpdateRequestsInstantSearch;
